/**
 * SlaCountdown — badge colorido com tempo até deadline.
 */
import { differenceInMinutes, formatDistanceToNow, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { AlertCircle, CheckCircle2, Clock, XCircle } from "lucide-react";

import { Badge } from "@/components/ui/badge";

interface SlaCountdownProps {
  deadlineAt: string | null;
  completedAt?: string | null;
  /** Se concluído no prazo, mostra "no prazo"; senão "atrasado" */
}

export function SlaCountdown({ deadlineAt, completedAt }: SlaCountdownProps) {
  if (!deadlineAt) {
    return <Badge variant="muted">Sem SLA</Badge>;
  }
  const deadline = parseISO(deadlineAt);

  // Concluído: comparar contra deadline
  if (completedAt) {
    const completed = parseISO(completedAt);
    const onTime = completed <= deadline;
    if (onTime) {
      return (
        <Badge variant="success" dot>
          <CheckCircle2 className="h-3 w-3" /> No prazo
        </Badge>
      );
    }
    const delay = differenceInMinutes(completed, deadline);
    return (
      <Badge variant="danger" dot title={`${delay} min de atraso`}>
        <XCircle className="h-3 w-3" /> Atrasado
      </Badge>
    );
  }

  // Em aberto: comparar contra agora
  const now = new Date();
  const minutesLeft = differenceInMinutes(deadline, now);

  if (minutesLeft < 0) {
    return (
      <Badge variant="danger" dot title={formatDistanceToNow(deadline, { locale: ptBR, addSuffix: true })}>
        <AlertCircle className="h-3 w-3" /> Estourou
      </Badge>
    );
  }
  if (minutesLeft < 60) {
    return (
      <Badge variant="warning" dot title={formatDistanceToNow(deadline, { locale: ptBR, addSuffix: true })}>
        <Clock className="h-3 w-3" /> {minutesLeft} min
      </Badge>
    );
  }
  if (minutesLeft < 60 * 4) {
    return (
      <Badge variant="warning" dot>
        <Clock className="h-3 w-3" /> {Math.floor(minutesLeft / 60)}h
      </Badge>
    );
  }
  return (
    <Badge variant="info" dot>
      <Clock className="h-3 w-3" /> {Math.floor(minutesLeft / 60)}h
    </Badge>
  );
}
